import React, { FC, useEffect, useRef, useState } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import { ResizeObserver } from 'resize-observer';
import { List as ImmutableList } from 'immutable';
import { yupResolver } from '@hookform/resolvers/yup';
import moment from 'moment-timezone';
import {
  Box,
  Button,
  Collapse,
  Divider,
  Grid,
  IconButton,
  SvgIcon,
  Typography,
} from '@material-ui/core';
import { makeStyles, Theme } from '@material-ui/core/styles';
import { ChevronRight, ExpandMore } from '@material-ui/icons';
import { ReactComponent as Times } from 'img/times.svg';
import { ButtonWithCircularProgress, ScrollBox, TextField } from 'common/components';
import { QuestionnaireModal } from 'common/modals';
import useRootSelector from 'common/hooks/useRootSelector';
import ColorPicker from './ColorPicker/ColorPicker';
import ScheduleSection from './ScheduleSection/ScheduleSection';
import ClubSection from './ClubSection/ClubSection';
import ParticipantsSection from './ParticipantsSection/ParticipantsSection';
import AdditionalSection from './AdditionalSection/AdditionalSection';
import { DictionaryList } from 'common/constants';
import { BookingEventValidationSchema } from 'modules/booking/components/EventForm/Schemas';
import { initialValuesEvent } from 'modules/booking/components/Modals/initialValues';
import { SenderAvailabilityTypeList } from 'modules/booking/constants/senderAvailability';

import {
  EventAttendeeType,
  EventOverbookingType,
  EventPersonType,
  EventRepeatEnd,
  EventRepeatFrequency,
  IBookingEvent,
  IEventFormValues,
  IEventPersonEntity,
  IEventPersonFormValue,
  IShortResourceImt,
} from 'modules/booking/interfaces';
import { INamedEntity, INamedEntityImt } from 'common/interfaces/common';
import { PeakModules } from 'common/constants/peakModules';
import { selectDictionaryList } from 'common/state/dictionary/selectors';
import { selectCurrentUserAvailableClubs } from 'modules/authentication/state/selectors';
import { useRenderIntlMessage } from 'common/hooks/useRenderIntlMessage';
import { getTimeFromMinutes } from 'common/utils/time';
import { getArray } from 'common/utils/typeUtils';
import useTimezoneMoment from 'common/hooks/useTimezoneMoment';
import messages from 'modules/booking/messages';
import commonMessages from 'common/messages/messages';
import inputLabels from 'common/messages/inputLabels';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
  },
  formWrapper: {
    overflow: 'hidden',
    padding: theme.spacing(1, 0),
  },
  formTitle: {
    padding: theme.spacing(2),
  },
  closeBtn: {
    width: '14px',
    height: '14px',
    position: 'absolute',
    top: '10px',
    right: '7px',
  },
  closeBtnIcon: {
    fontSize: '14px',
    opacity: '0.3',
  },
  title: {
    lineHeight: '21px',
    letterSpacing: 0,
  },
  nameField: {
    position: 'relative',
  },
  picker: {
    position: 'absolute',
    top: '28px',
    right: '24px',
  },
  sectionDivider: {
    paddingTop: 0,
    paddingBottom: 0,
  },
  sectionBtn: {
    padding: '6px 0',
    justifyContent: 'flex-start',
    color: theme.palette.text.secondary,
  },
  inputBlock: {
    flex: 1,
    minHeight: 0,
  },
  submitRow: {
    padding: theme.spacing(2),
  },
}));

interface IEventForm {
  onCancel: () => void;
  onSubmit: (formValues: IEventFormValues) => void;
  onLoadResources: (clubId: string) => void;
  handleOverflow: (value) => void;
  clearSearchServicesResult: (personId?: number) => void;
  searchServices: (search: string, personId?: number) => void;
  initialData?: Partial<IEventFormValues>;
  event?: IBookingEvent;
  isSubmitting: boolean;
  personId?: number;
  selectedClubId?: string;
  type: SenderAvailabilityTypeList;
  module?: PeakModules;
}

const EventForm: FC<IEventForm> = ({
  event,
  initialData = {},
  isSubmitting,
  onCancel,
  onSubmit,
  onLoadResources,
  handleOverflow,
  personId,
  clearSearchServicesResult,
  searchServices,
  selectedClubId,
  module,
  type,
}) => {
  const formRef = useRef(null);
  const navigate = useNavigate();
  const classes = useStyles();
  const renderErrorMessage = useRenderIntlMessage();
  const [timezoneMoment] = useTimezoneMoment();

  const [showParticipantsSection, setShowParticipantsSection] = useState<boolean>(false);
  const [showAdditionalSection, setShowAdditionalSection] = useState<boolean>(false);
  const [isOpenChangeAllowedPtsModal, setIsOpenAllowedPtsModal] = useState<boolean>(false);
  const [updatedAllowedPts = [], setUpdatedAllowedPts] = useState<EventAttendeeType[] | null>(null);
  const [formHeight, setFormHeight] = useState<number>(0);

  const resources: ImmutableList<IShortResourceImt> = useRootSelector(
    selectDictionaryList(DictionaryList.RESOURCES),
  );
  const clubs: ImmutableList<INamedEntityImt> = useRootSelector(selectCurrentUserAvailableClubs);

  // TODO-3868: Check endDate if needed
  // const initialDates = useMemo(() => {
  //   const nowDate = timezoneMoment()
  //     .add(10, 'minutes')
  //     .utc();
  //
  //   return {
  //     date: nowDate.format('YYYY-MM-DD'),
  //     time: nowDate.format('HH:mm'),
  //     endDate: nowDate.format(),
  //   };
  // }, [timezoneMoment]);

  const getSelectedClub = (): INamedEntity | null => {
    const selectedClub = clubs.find(item => item.get('id') === selectedClubId);
    return selectedClub ? selectedClub.toJS() : null;
  };

  const getDateNowMoment = (): moment.Moment => {
    const dateNow = timezoneMoment().utc();
    const offset = 15 - (dateNow.minute() % 15);

    return dateNow.add(offset || 15, 'minutes').utc();
  };

  const getInitialValues = (): IEventFormValues => {
    let initialValues: IEventFormValues = {
      ...initialValuesEvent,
      ...initialData,
    };

    if (event) {
      const duration = event.durationInMinutes;
      const reminder = !!event.reminderPeriodType;
      const eventPersons = getArray<IEventPersonEntity>(event.persons);

      const persons = eventPersons.map(person => {
        const newPerson: IEventPersonFormValue = {
          id: person.id,
          joinedDate: person.joinedDate,
          attendanceType: person.attendanceType,
          status: person.status,
          type: person.type || EventPersonType.CUSTOMER,
          resourceId: person.resourceId,
        };

        if (person.eventDate) {
          newPerson.eventDate = person.eventDate;
        }

        if (person.customer) {
          newPerson.customer = person.customer;
        } else if (person.salesperson) {
          newPerson.salesperson = person.salesperson;
        }

        return newPerson;
      });

      initialValues = {
        ...initialValues,
        ...event,
        persons,
        durationInMinutes: getTimeFromMinutes(Number(duration)),
        reminder,
        repeatingFrequency: event.repeatingFrequency || EventRepeatFrequency.DAILY,
        repeatingDurationType: event.repeatingDurationType || EventRepeatEnd.FOREVER,
        overbookingType: event.overbookingType || EventOverbookingType.WAIT_LIST,
      };
    }

    if (!initialValues.date) {
      initialValues.date = getDateNowMoment().format('YYYY-MM-DD');
    }

    if (!initialValues.time) {
      initialValues.time = getDateNowMoment().format('HH:mm');
    }

    if (!initialValues.durationInMinutes) {
      initialValues.durationInMinutes = getTimeFromMinutes(60);
    }

    if (!initialValues.club) {
      initialValues.club = getSelectedClub();
    }

    return initialValues;
  };

  const formMethods = useForm<IEventFormValues>({
    defaultValues: getInitialValues(),
    resolver: yupResolver(BookingEventValidationSchema(!!selectedClubId)) as any, // TODO - PRM-1810 need resolver type
    mode: 'all',
  });

  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    getValues,
  } = formMethods;

  const club = watch('club');
  const clubId = club?.id;

  useEffect(() => {
    if (clubId) {
      onLoadResources(clubId);
    }
  }, [clubId, onLoadResources]);

  useEffect(() => {
    const observedRef = formRef.current;
    const resizeObserver = new ResizeObserver(entries => {
      const boxHeight = entries[0].contentRect.height;
      const isBlockOverflow = window.innerHeight - 178 < boxHeight;

      if (isBlockOverflow) {
        setFormHeight(boxHeight);
      }
      handleOverflow(isBlockOverflow);
    });
    if (observedRef) {
      resizeObserver.observe(observedRef);
    }
    return () => {
      if (observedRef) {
        resizeObserver.unobserve(observedRef);
      }
    };
  }, [formRef, handleOverflow]);

  useEffect(() => {
    const resizeFunction = () => {
      if (window.innerHeight - 178 < formHeight) {
        handleOverflow(true);
      } else {
        handleOverflow(false);
      }
    };

    window.addEventListener('resize', resizeFunction);
    return () => {
      window.removeEventListener('resize', resizeFunction);
    };
  }, [formHeight, handleOverflow, showParticipantsSection]);

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    } else {
      navigate(-1);
    }
  };

  const handleAllowedParticipantsChange = (types: Array<EventAttendeeType>) => {
    const { allowedParticipantTypes = [] } = getValues();
    if (allowedParticipantTypes.length < types.length) {
      setValue('allowedParticipantTypes', types);
    } else {
      setIsOpenAllowedPtsModal(true);
      setUpdatedAllowedPts(types);
    }
  };

  const handleChangeAllowedParticipantsModalSubmit = () => {
    const persons = getValues('persons') || [];

    if (persons.length) {
      setValue(
        'persons',
        persons.filter(personItem => {
          if (personItem.salesperson?.type === EventAttendeeType.RESOURCE_EMPLOYEE) {
            return true;
          }

          let eventAttendeeType: EventAttendeeType | null = null;

          if (personItem.customer?.type) {
            eventAttendeeType = personItem.customer.type;
          } else if (personItem.salesperson) {
            eventAttendeeType = EventAttendeeType.EMPLOYEE;
          }

          if (eventAttendeeType) {
            return (updatedAllowedPts || []).includes(eventAttendeeType);
          }

          return false;
        }),
      );
    }

    setValue('allowedParticipantTypes', updatedAllowedPts || []);

    setIsOpenAllowedPtsModal(false);
    setUpdatedAllowedPts(null);
  };

  const handleSubmitEventForm = (data: IEventFormValues) => {
    onSubmit({
      ...data,
      persons: data.persons?.map(item => {
        const clonedPerson = { ...item };
        delete clonedPerson.resourceId;
        return clonedPerson;
      }),
    });
  };

  return (
    <>
      <FormProvider {...formMethods}>
        <form
          id="event-form"
          className={classes.root}
          autoComplete="none"
          onSubmit={handleSubmit(handleSubmitEventForm)}
        >
          <Box className={classes.formTitle}>
            <IconButton className={classes.closeBtn} size="small" onClick={handleCancel}>
              <SvgIcon className={classes.closeBtnIcon}>
                <Times />
              </SvgIcon>
            </IconButton>

            <Typography className={classes.title} variant="h4">
              {!!event && !!event.id ? (
                <FormattedMessage {...messages.editEvent} />
              ) : (
                <FormattedMessage {...messages.newEvent} />
              )}
            </Typography>
          </Box>

          <Box className={classes.inputBlock}>
            <ScrollBox suppressScrollX hasShadowsOnScroll>
              <div ref={formRef} className={classes.formWrapper}>
                <Box pl={2} pr={2}>
                  <Grid container spacing={3}>
                    <Grid item className={classes.nameField} xs={12}>
                      <Controller
                        name="title"
                        control={control}
                        render={({ field: { onChange, onBlur, value } }) => (
                          <TextField
                            fullWidth
                            variant="outlined"
                            autoComplete="none"
                            onChange={onChange}
                            onBlur={onBlur}
                            label={<FormattedMessage {...inputLabels.name} />}
                            value={value}
                            error={!!errors.title}
                            helperText={renderErrorMessage(errors?.title?.message)}
                          />
                        )}
                      />

                      <Controller
                        name="color"
                        control={control}
                        render={({ field: { value, onChange } }) => (
                          <ColorPicker
                            value={value}
                            onChange={onChange}
                            className={classes.picker}
                            error={!!errors.color}
                            helperText={renderErrorMessage(errors?.color?.message)}
                          />
                        )}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <ScheduleSection />
                    </Grid>

                    <Grid item xs={12}>
                      <ClubSection
                        personId={personId}
                        resources={resources}
                        clearSearchResults={clearSearchServicesResult}
                        searchServices={searchServices}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <Grid container spacing={1}>
                        <Grid item className={classes.sectionDivider} xs={12}>
                          <Divider />
                        </Grid>

                        <Grid item xs={12}>
                          <Button
                            className={classes.sectionBtn}
                            color="secondary"
                            fullWidth
                            onClick={() => setShowParticipantsSection(!showParticipantsSection)}
                            startIcon={
                              showParticipantsSection ? (
                                <ExpandMore fontSize="large" />
                              ) : (
                                <ChevronRight fontSize="large" />
                              )
                            }
                          >
                            <FormattedMessage {...inputLabels.participants} />
                          </Button>

                          <Collapse
                            in={showParticipantsSection || !!errors.allowedParticipantTypes}
                          >
                            <ParticipantsSection
                              personId={personId}
                              module={module}
                              resources={resources}
                              onAllowedParticipantsChange={handleAllowedParticipantsChange}
                            />
                          </Collapse>
                        </Grid>

                        <Grid item className={classes.sectionDivider} xs={12}>
                          <Divider />
                        </Grid>

                        <Grid item xs={12}>
                          <Button
                            className={classes.sectionBtn}
                            color="secondary"
                            fullWidth
                            onClick={() => setShowAdditionalSection(!showAdditionalSection)}
                            startIcon={
                              showAdditionalSection ? (
                                <ExpandMore fontSize="large" />
                              ) : (
                                <ChevronRight fontSize="large" />
                              )
                            }
                          >
                            <FormattedMessage {...messages.additional} />
                          </Button>
                          <Collapse in={showAdditionalSection}>
                            <AdditionalSection type={type} />
                          </Collapse>
                        </Grid>

                        <Grid item className={classes.sectionDivider} xs={12}>
                          <Divider />
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                </Box>
              </div>
            </ScrollBox>
          </Box>

          <Box className={classes.submitRow}>
            <Grid container justifyContent="flex-end" spacing={1}>
              <Grid item>
                <Button onClick={handleCancel} color="primary">
                  <FormattedMessage {...commonMessages.cancelBtn} />
                </Button>
              </Grid>

              <Grid item>
                <ButtonWithCircularProgress isSubmitting={isSubmitting}>
                  <FormattedMessage {...commonMessages.saveBtn} />
                </ButtonWithCircularProgress>
              </Grid>
            </Grid>
          </Box>
        </form>
      </FormProvider>

      {isOpenChangeAllowedPtsModal ? (
        <QuestionnaireModal
          isOpen
          onClose={() => setIsOpenAllowedPtsModal(false)}
          onSubmit={handleChangeAllowedParticipantsModalSubmit}
          title={<FormattedMessage {...messages.approveChangeAllowedParticipantsTitle} />}
          body={<FormattedMessage {...messages.approveChangeAllowedParticipantsMsg} />}
          submitBtnTitle={<FormattedMessage {...commonMessages.continueBtn} />}
        />
      ) : null}
    </>
  );
};

export default EventForm;
